import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, TextField, FormControl, InputLabel, Select,
    MenuItem, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    Tabs, Tab, Chip, Divider
} from '@mui/material';
import { Add, Edit, Delete, Visibility, Print, Search, Download } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

const InwardOutward = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [entries, setEntries] = useState([]);
    const [letterTypes, setLetterTypes] = useState([]);
    const [parties, setParties] = useState([]);
    const [locations, setLocations] = useState([]);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [openMasterDialog, setOpenMasterDialog] = useState(false);
    const [masterType, setMasterType] = useState('');
    const [editEntry, setEditEntry] = useState(null);

    const [filters, setFilters] = useState({
        type: 'outward',
        fromDate: '',
        toDate: '',
        letterTypeId: '',
        partyId: ''
    });

    const [entryForm, setEntryForm] = useState({
        type: 'outward',
        registerNumber: '',
        date: new Date().toISOString().split('T')[0],
        letterTypeId: '',
        partyId: '',
        locationId: '',
        subject: '',
        remarks: '',
        fileReference: ''
    });

    const [masterForm, setMasterForm] = useState({ name: '', code: '', description: '' });

    useEffect(() => {
        fetchMasterData();
    }, []);

    useEffect(() => {
        fetchEntries();
    }, [filters.type]);

    const fetchMasterData = async () => {
        try {
            // Fetch all master data - using generic endpoint or specific ones
            const [typesRes, partiesRes, locationsRes, unitsRes] = await Promise.all([
                api.get('/master-data', { params: { type: 'letter_type' } }).catch(() => ({ data: { data: [] } })),
                api.get('/master-data', { params: { type: 'party' } }).catch(() => ({ data: { data: [] } })),
                api.get('/master-data', { params: { type: 'location' } }).catch(() => ({ data: { data: [] } })),
                api.get('/master-data', { params: { type: 'unit' } }).catch(() => ({ data: { data: [] } }))
            ]);

            setLetterTypes(typesRes.data.data || []);
            setParties(partiesRes.data.data || []);
            setLocations(locationsRes.data.data || []);
            setUnits(unitsRes.data.data || []);
        } catch (error) {
            console.error('Failed to fetch master data');
        }
    };

    const fetchEntries = async () => {
        setLoading(true);
        try {
            const res = await api.get('/letters', { params: { type: filters.type } });
            setEntries(res.data.data || []);
        } catch (error) {
            console.error('Failed to fetch entries');
            // Mock data for display
            setEntries([
                { id: 1, registerNumber: 'OUT-001', date: '2025-01-15', subject: 'Fee Reminder', partyName: 'Parents', status: 'sent' },
                { id: 2, registerNumber: 'OUT-002', date: '2025-01-14', subject: 'Result Notice', partyName: 'Education Board', status: 'pending' }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddEntry = () => {
        setEditEntry(null);
        setEntryForm({
            type: filters.type,
            registerNumber: '',
            date: new Date().toISOString().split('T')[0],
            letterTypeId: '',
            partyId: '',
            locationId: '',
            subject: '',
            remarks: '',
            fileReference: ''
        });
        setOpenDialog(true);
    };

    const handleEditEntry = (entry) => {
        setEditEntry(entry);
        setEntryForm({
            type: entry.type,
            registerNumber: entry.registerNumber,
            date: entry.date,
            letterTypeId: entry.letterTypeId,
            partyId: entry.partyId,
            locationId: entry.locationId,
            subject: entry.subject,
            remarks: entry.remarks,
            fileReference: entry.fileReference
        });
        setOpenDialog(true);
    };

    const handleSaveEntry = async () => {
        try {
            if (editEntry) {
                await api.put(`/letters/${editEntry.id}`, entryForm);
                toast.success('Entry updated');
            } else {
                await api.post('/letters', entryForm);
                toast.success('Entry created');
            }
            setOpenDialog(false);
            fetchEntries();
        } catch (error) {
            toast.error('Failed to save entry');
        }
    };

    const handleDeleteEntry = async (id) => {
        if (!window.confirm('Delete this entry?')) return;
        try {
            await api.delete(`/letters/${id}`);
            toast.success('Entry deleted');
            fetchEntries();
        } catch (error) {
            toast.error('Failed to delete entry');
        }
    };

    const handleAddMaster = (type) => {
        setMasterType(type);
        setMasterForm({ name: '', code: '', description: '' });
        setOpenMasterDialog(true);
    };

    const handleSaveMaster = async () => {
        try {
            await api.post('/master-data', { ...masterForm, type: masterType });
            toast.success('Master data added');
            setOpenMasterDialog(false);
            fetchMasterData();
        } catch (error) {
            toast.error('Failed to save master data');
        }
    };

    const renderFilters = () => (
        <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Letter Type</InputLabel>
                        <Select
                            value={filters.letterTypeId}
                            label="Letter Type"
                            onChange={e => setFilters({ ...filters, letterTypeId: e.target.value })}
                        >
                            <MenuItem value="">All</MenuItem>
                            {letterTypes.map(t => (
                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>{filters.type === 'outward' ? 'To Party' : 'From Party'}</InputLabel>
                        <Select
                            value={filters.partyId}
                            label="Party"
                            onChange={e => setFilters({ ...filters, partyId: e.target.value })}
                        >
                            <MenuItem value="">All</MenuItem>
                            {parties.map(p => (
                                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField
                        fullWidth size="small" label="From Date" type="date"
                        value={filters.fromDate}
                        onChange={e => setFilters({ ...filters, fromDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField
                        fullWidth size="small" label="To Date" type="date"
                        value={filters.toDate}
                        onChange={e => setFilters({ ...filters, toDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField
                        fullWidth size="small" label="Search Subject"
                        placeholder="Search..."
                    />
                </Grid>
                <Grid item xs={12} md={2}>
                    <Button variant="contained" startIcon={<Search />} onClick={fetchEntries}>
                        Search
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );

    const renderMasterDataTab = () => (
        <Grid container spacing={3}>
            {/* Letter Types */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">Letter Types</Typography>
                        <Button size="small" startIcon={<Add />} onClick={() => handleAddMaster('letter_type')}>
                            Add Type
                        </Button>
                    </Box>
                    <TableContainer sx={{ maxHeight: 300 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {letterTypes.map(t => (
                                    <TableRow key={t.id}>
                                        <TableCell>{t.name}</TableCell>
                                        <TableCell>{t.code}</TableCell>
                                        <TableCell>
                                            <IconButton size="small"><Edit fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error"><Delete fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>

            {/* Parties */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">Parties (From/To)</Typography>
                        <Button size="small" startIcon={<Add />} onClick={() => handleAddMaster('party')}>
                            Add Party
                        </Button>
                    </Box>
                    <TableContainer sx={{ maxHeight: 300 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {parties.map(p => (
                                    <TableRow key={p.id}>
                                        <TableCell>{p.name}</TableCell>
                                        <TableCell><Chip label={p.type || 'both'} size="small" /></TableCell>
                                        <TableCell>
                                            <IconButton size="small"><Edit fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error"><Delete fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>

            {/* Locations */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">Locations</Typography>
                        <Button size="small" startIcon={<Add />} onClick={() => handleAddMaster('location')}>
                            Add Location
                        </Button>
                    </Box>
                    <TableContainer sx={{ maxHeight: 200 }}>
                        <Table size="small">
                            <TableBody>
                                {locations.map(l => (
                                    <TableRow key={l.id}>
                                        <TableCell>{l.name}</TableCell>
                                        <TableCell>
                                            <IconButton size="small"><Edit fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error"><Delete fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>

            {/* Units */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">Units</Typography>
                        <Button size="small" startIcon={<Add />} onClick={() => handleAddMaster('unit')}>
                            Add Unit
                        </Button>
                    </Box>
                    <TableContainer sx={{ maxHeight: 200 }}>
                        <Table size="small">
                            <TableBody>
                                {units.map(u => (
                                    <TableRow key={u.id}>
                                        <TableCell>{u.name}</TableCell>
                                        <TableCell>
                                            <IconButton size="small"><Edit fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error"><Delete fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>
        </Grid>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Inward / Outward Register</Typography>

            <Paper sx={{ mb: 2 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                    <Tab label="Outward Register" />
                    <Tab label="Inward Register" />
                    <Tab label="Master Data" />
                    <Tab label="Reports" />
                </Tabs>
            </Paper>

            {(activeTab === 0 || activeTab === 1) && (
                <>
                    {renderFilters()}

                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6">
                                {activeTab === 0 ? 'Outward' : 'Inward'} Entries ({entries.length})
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button variant="outlined" startIcon={<Download />}>Export</Button>
                                <Button variant="contained" startIcon={<Add />} onClick={handleAddEntry}>
                                    Add Entry
                                </Button>
                            </Box>
                        </Box>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Reg. No.</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Subject</TableCell>
                                        <TableCell>{activeTab === 0 ? 'To Party' : 'From Party'}</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {entries.map(e => (
                                        <TableRow key={e.id}>
                                            <TableCell>{e.registerNumber}</TableCell>
                                            <TableCell>{e.date}</TableCell>
                                            <TableCell>{e.subject}</TableCell>
                                            <TableCell>{e.partyName}</TableCell>
                                            <TableCell>
                                                <Chip label={e.status || 'pending'} size="small" color={e.status === 'sent' ? 'success' : 'warning'} />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton size="small" onClick={() => handleEditEntry(e)}><Edit fontSize="small" /></IconButton>
                                                <IconButton size="small"><Visibility fontSize="small" /></IconButton>
                                                <IconButton size="small"><Print fontSize="small" /></IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDeleteEntry(e.id)}><Delete fontSize="small" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}

            {activeTab === 2 && renderMasterDataTab()}

            {activeTab === 3 && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>Inward/Outward Reports</Typography>
                    <Typography color="text.secondary">Report generation options will appear here.</Typography>
                </Paper>
            )}

            {/* Entry Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editEntry ? 'Edit Entry' : 'Add Entry'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth label="Register Number" value={entryForm.registerNumber}
                                onChange={e => setEntryForm({ ...entryForm, registerNumber: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth label="Date" type="date" value={entryForm.date}
                                onChange={e => setEntryForm({ ...entryForm, date: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Letter Type</InputLabel>
                                <Select
                                    value={entryForm.letterTypeId}
                                    label="Letter Type"
                                    onChange={e => setEntryForm({ ...entryForm, letterTypeId: e.target.value })}
                                >
                                    {letterTypes.map(t => (
                                        <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>{filters.type === 'outward' ? 'To Party' : 'From Party'}</InputLabel>
                                <Select
                                    value={entryForm.partyId}
                                    label="Party"
                                    onChange={e => setEntryForm({ ...entryForm, partyId: e.target.value })}
                                >
                                    {parties.map(p => (
                                        <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth label="Subject" value={entryForm.subject}
                                onChange={e => setEntryForm({ ...entryForm, subject: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth label="Remarks" multiline rows={2} value={entryForm.remarks}
                                onChange={e => setEntryForm({ ...entryForm, remarks: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveEntry}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Master Data Dialog */}
            <Dialog open={openMasterDialog} onClose={() => setOpenMasterDialog(false)}>
                <DialogTitle>Add {masterType.replace('_', ' ')}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth label="Name" margin="normal" value={masterForm.name}
                        onChange={e => setMasterForm({ ...masterForm, name: e.target.value })}
                    />
                    <TextField
                        fullWidth label="Code" margin="normal" value={masterForm.code}
                        onChange={e => setMasterForm({ ...masterForm, code: e.target.value })}
                    />
                    <TextField
                        fullWidth label="Description" margin="normal" multiline rows={2} value={masterForm.description}
                        onChange={e => setMasterForm({ ...masterForm, description: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenMasterDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveMaster}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InwardOutward;
